import { AppDispatch, useAppSelector } from 'app/store';
import { apiUrls } from 'app/api.config';
import { Settings } from 'types/general.types';
import { fetchSettingsEffect } from 'app/store/Settings/effects';
import { setSettings } from 'app/store/Settings/slice';
import { useEffect, useRef } from 'react';
import { isWebClient } from 'app/utils/helpers';
import { useIntercom } from 'react-use-intercom';
import { getIntercomUserHash } from 'app/store/Intercom/selectors';
import { getUserGuid } from 'app/store/Auth/selectors';
import { useLocation } from 'react-router-dom';
import { RetryableSocket } from '../webSockets';

export function useAssistSettingsWs(dispatch: AppDispatch) {
  const ws = useRef<RetryableSocket | null>(null);

  useEffect(() => {
    if (isWebClient || ws.current !== null) return () => {};

    ws.current = new RetryableSocket(
      apiUrls.settingsChanges,
      () => dispatch(fetchSettingsEffect()).unwrap(),
      (m) => {
        const newSettings = JSON.parse(m.data) as Settings;
        // if JSON parsing fails here, uncaught error will be seen in console but ws will continue working
        dispatch(setSettings(newSettings));
      }
    );
    const wsCurrent = ws.current;
    return () => {
      wsCurrent.close();
      ws.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}

export const useSyncIntercom = (isAuthenticated: boolean) => {
  const { boot, update } = useIntercom();
  const intercomUserHash = useAppSelector(getIntercomUserHash);
  const currentUserGuid = useAppSelector(getUserGuid);
  const { pathname } = useLocation();
  useEffect(() => {
    boot({
      hideDefaultLauncher: true,
    });
  }, [boot, isAuthenticated]);

  useEffect(() => {
    if (intercomUserHash) {
      update({
        userHash: intercomUserHash?.web,
        userId: currentUserGuid,
        lastRequestAt: (new Date().getTime() / 1000).toString(),
      });
    } else {
      update();
    }
  }, [update, intercomUserHash, isAuthenticated, currentUserGuid, pathname]);
};
